import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { waterSewerProviderInfoService } from '../../../../services/waterSewerProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { redactError } from '../../../../utils/redactError';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('WaterSewerProviderData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const WaterSewerProviderDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [waterSewerProviderData, setWaterSewerProviderData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadWaterSewerProviderData();
  }, []);

  const loadWaterSewerProviderData = async () => {
    try {
      setLoading(true);
      logger.debug('[Debug] Starting to load water sewer provider data...');
      const data = await waterSewerProviderInfoService.getAllWaterSewerProviderInfo(targetUserId);
      logger.debug('[Debug] Service returned data:', data);
      logger.debug('[Debug] Data length:', data?.length);
      logger.debug('[Debug] Data type:', typeof data);
      logger.debug('[Debug] Is array:', Array.isArray(data));
      
      if (data && Array.isArray(data) && data.length > 0) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials for each water sewer provider
          const updatedData = await Promise.all(
            data.map(async (waterSewerProvider) => {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for water sewer provider ID:', waterSewerProvider.id);
                const portalData = await vaultService.getPortalCredentials(waterSewerProvider.id, 'water-sewer-provider');
                logger.debug('[Portal Debug] Portal data retrieved for water sewer provider:', waterSewerProvider.id, portalData);
                if (portalData) {
                  waterSewerProvider.username = portalData.username;
                  waterSewerProvider.password = portalData.password;
                  waterSewerProvider.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to water sewer provider data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for water sewer provider:', waterSewerProvider.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for water sewer provider:', waterSewerProvider.id, vaultError);
              }
              return waterSewerProvider;
            })
          );

          logger.debug('[Data Debug] Final water sewer provider data being set:', updatedData.length, 'providers');
          logger.debug('[Data Debug] First provider account number:', updatedData[0]?.accountNumber);
          setWaterSewerProviderData(updatedData);
        } else {
          setWaterSewerProviderData(data);
        }
      } else {
        logger.debug('[Debug] No data returned or empty array');
        setWaterSewerProviderData([]);
      }
    } catch (error: any) {
      logger.error('[Debug] Error loading water sewer provider data:', redactError(error));
      Alert.alert('Error', 'Failed to load water & sewer provider information: ' + (error?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const handleEditWaterSewerProvider = (waterSewerProvider: any) => {
    navigation.navigate('WaterSewerProviderInput', { 
      waterSewerProviderId: waterSewerProvider.id, 
      isEdit: true 
    });
  };

  const handleDeleteWaterSewerProvider = async (waterSewerProvider: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this water & sewer provider information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await waterSewerProviderInfoService.deleteWaterSewerProviderInfo(waterSewerProvider.id);
            
            // Reload data after deletion
            await loadWaterSewerProviderData();
            
            Alert.alert('Deleted', 'Water & sewer provider information deleted.');
          } catch (e) {
            logger.error('Delete water sewer provider info failed:', e);
            Alert.alert('Error', 'Failed to delete water & sewer provider information');
          }
        } 
      },
    ]);
  };

  const handleAddWaterSewerProvider = () => {
    navigation.navigate('WaterSewerProviderInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Water & Sewer Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading water & sewer provider information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!waterSewerProviderData || waterSewerProviderData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Water & Sewer Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No water & sewer provider information found</Text>
            <TouchableOpacity
              style={styles.addButton}
              onPress={handleAddWaterSewerProvider}
            >
              <Text style={styles.addButtonText}>Add Water & Sewer Provider Info</Text>
            </TouchableOpacity>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Water & Sewer Provider Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {waterSewerProviderData.map((waterSewerProvider, index) => (
            <View key={waterSewerProvider.id || index} style={styles.waterSewerProviderCard}>
              <View style={styles.waterSewerProviderHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.waterSewerProviderName}>
                    {waterSewerProvider.companyName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.waterSewerProviderType}>
                    {waterSewerProvider.serviceType || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editWaterSewerProviderButton}
                      onPress={() => handleEditWaterSewerProvider(waterSewerProvider)}
                    >
                      <Text style={styles.editWaterSewerProviderButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteWaterSewerProviderButton}
                      onPress={() => handleDeleteWaterSewerProvider(waterSewerProvider)}
                    >
                      <Text style={styles.deleteWaterSewerProviderButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Company Name" value={waterSewerProvider.companyName || ''} />
              <InfoItem label="Service Type" value={waterSewerProvider.serviceType || ''} />
              
              {waterSewerProvider.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[waterSewerProvider.id] ? waterSewerProvider.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [waterSewerProvider.id]: !prev[waterSewerProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[waterSewerProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              <InfoItem label="Service Address" value={waterSewerProvider.serviceAddress || ''} />
              <InfoItem label="Billing Address" value={waterSewerProvider.billingAddress || ''} />
              <InfoItem label="Customer Service Phone" value={formatPhoneNumber(waterSewerProvider.customerServicePhone) || ''} />
              <InfoItem label="Emergency Phone" value={formatPhoneNumber(waterSewerProvider.emergencyPhone) || ''} />
              <InfoItem label="Website" value={waterSewerProvider.website || ''} />
              <InfoItem label="Billing Cycle" value={waterSewerProvider.billingCycle || ''} />
              <InfoItem label="Monthly Cost" value={formatCurrency(waterSewerProvider.monthlyCost) || ''} />
              <InfoItem label="Autopay Enabled" value={waterSewerProvider.autopayEnabled ? 'Yes' : 'No'} />
              <InfoItem label="Autopay Method" value={waterSewerProvider.autopayMethod || ''} />
              <InfoItem label="Meter Number" value={waterSewerProvider.meterNumber || ''} />
              <InfoItem label="Shutoff Location" value={waterSewerProvider.shutoffLocation || ''} />
              
              {waterSewerProvider.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[waterSewerProvider.id] ? waterSewerProvider.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [waterSewerProvider.id]: !prev[waterSewerProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[waterSewerProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {waterSewerProvider.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[waterSewerProvider.id] ? waterSewerProvider.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [waterSewerProvider.id]: !prev[waterSewerProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[waterSewerProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={waterSewerProvider.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherWaterSewerProviderButton}
                onPress={handleAddWaterSewerProvider}
              >
                <Text style={styles.addAnotherWaterSewerProviderButtonText}>Add Another Water & Sewer Provider</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.goBack()}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  waterSewerProviderCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  waterSewerProviderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  waterSewerProviderName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  waterSewerProviderType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editWaterSewerProviderButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editWaterSewerProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteWaterSewerProviderButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteWaterSewerProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherWaterSewerProviderButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherWaterSewerProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});